import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Sparkles, Loader2, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AmbassadorBadge from "@/components/AmbassadorBadge";

/**
 * AmbassadorLeaderboard — public page ranking approved ambassadors by
 * fresh referrals. Two scopes: this month (default) or all-time.
 *
 * The page is intentionally standalone (no login required) so it can be
 * shared on social to advertise the program. Anyone hitting /ambassadors
 * sees who's winning + a clear "Apply to be an Ambassador" CTA.
 */
export default function AmbassadorLeaderboard() {
  const [scope, setScope] = useState("month");
  const [data, setData] = useState({ scope: "month", rows: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/ambassadors/leaderboard", { params: { scope, limit: 25 } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ scope, rows: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope]);

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="ambassador-leaderboard-page">
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <Link
          to="/feed"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"
        >
          <ChevronLeft size={14} /> Back
        </Link>

        <div className="text-center py-6">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-amber-400">
            <Trophy size={14} /> Panda Ambassadors
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight">
            Top of the pack.
          </h1>
          <p className="text-zinc-400 text-sm mt-3 max-w-lg mx-auto leading-relaxed">
            Every referred sign-up moves an ambassador up. The higher they climb,
            the brighter their pin — and the fatter their monthly coin drop.
          </p>
        </div>

        {/* Scope toggle */}
        <div className="flex justify-center gap-2 mb-6">
          {[
            { key: "month", label: "This month" },
            { key: "all_time", label: "All time" },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              data-testid={`leaderboard-scope-${s.key}`}
              className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition ${
                scope === s.key
                  ? "bg-amber-400 border-amber-300 text-black font-semibold"
                  : "bg-black/40 border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-500">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading leaderboard…
          </div>
        ) : data.rows.length === 0 ? (
          <div className="text-center text-zinc-500 py-12">
            No ambassadors yet {scope === "month" ? "this month" : ""}.
            Be the first.
          </div>
        ) : (
          <div className="space-y-2" data-testid="leaderboard-rows">
            {data.rows.map((row, i) => (
              <LeaderboardRow key={row.id} row={row} rank={i + 1} scope={scope} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 border-t border-white/10 pt-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-2">
            <Sparkles size={12} /> Want your name on this list?
          </div>
          <Link
            to="/ambassadors/apply"
            className="inline-block bg-gradient-to-r from-yellow-400 to-amber-500 hover:brightness-110 text-black font-semibold rounded-full px-6 py-2.5 text-sm"
            data-testid="leaderboard-cta-apply"
          >
            Apply to be a Panda Ambassador
          </Link>
        </div>
      </div>
    </div>
  );
}

function LeaderboardRow({ row, rank, scope }) {
  const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
  return (
    <Link
      to={`/u/${row.id}`}
      data-testid={`leaderboard-row-${row.id}`}
      className="flex items-center gap-3 bg-zinc-950/60 border border-white/10 hover:border-amber-500/40 rounded-md px-4 py-3 transition-colors"
    >
      <div className="w-8 text-center text-lg text-zinc-500 font-display">
        {medal || `#${rank}`}
      </div>
      <Avatar className="w-10 h-10 border border-white/10">
        <AvatarImage src={row.avatar_url} />
        <AvatarFallback className="bg-rose-600 text-white text-xs">
          {row.display_name?.[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="text-sm text-white truncate">{row.display_name || row.handle}</div>
          <AmbassadorBadge status="approved" tier={row.ambassador_tier} />
        </div>
        <div className="text-[11px] text-zinc-500">{row.display_name}</div>
      </div>
      <div className="text-right">
        <div className="text-lg text-amber-300 font-display leading-tight">
          {row.score.toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {scope === "month" ? "this month" : "all time"}
        </div>
      </div>
    </Link>
  );
}
